import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentJointDisclosuresData,
  type EnrollmentJointDisclosuresSubmitInput,
  type EnrollmentJointDisclosuresSubmitOutput,
  type EnrollmentJointDisclosuresGetInput,
} from '../../../../models'
import { createMutationHook, createQueryHook } from '../../../../hooks'

/**
 * Get Enrollment Joint Disclosures data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentJointDisclosuresData>
 * @example const response = await getEnrollmentJointDisclosuresData()
 */
export const getEnrollmentJointDisclosuresData = async (
  input?: EnrollmentJointDisclosuresGetInput,
  context?: DefaultContext,
): Promise<EnrollmentJointDisclosuresData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentJointDisclosuresData',
      query: `mutation getEnrollmentJointDisclosuresData($input: SubmitJointEnrollmentInput!) {
        submitJointEnrollment(input: $input) {
          errors
          isError
          activePerson {
            applicationId
            path
            progress
            name {
              firstName
            }
            unacceptedInvestmentDisclosures {
              disclosureId
              disclosureName
              disclosureUri
            }
          }
        }
      }`,
    },
    {
      input: input ?? {},
    },
    context,
  )) as { submitJointEnrollment: EnrollmentJointDisclosuresData }
  return response.submitJointEnrollment
}

export const useGetEnrollmentJointDisclosuresData = createQueryHook<
  EnrollmentJointDisclosuresGetInput,
  EnrollmentJointDisclosuresData
>(getEnrollmentJointDisclosuresData)

/**
 * Submit Enrollment Joint Disclosures data
 * @param enrollmentDisclosuresSubmitInput - The disclosures data to submit
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentJointDisclosuresSubmitOutput>
 * @example const response = await submitEnrollmentJointDisclosuresData(disclosuresData);
 */
export const submitEnrollmentJointDisclosuresData = async (
  enrollmentDisclosuresSubmitInput: EnrollmentJointDisclosuresSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentJointDisclosuresSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentJointDisclosuresData',
      query: `mutation submitEnrollmentJointDisclosuresData($input: SubmitJointEnrollmentInput!) {
        submitJointEnrollment(input: $input) {
          errors
          isError
          activePerson {
            path
          }
        }
      }`,
    },
    {
      input: enrollmentDisclosuresSubmitInput,
    },
    context,
  )) as { submitJointEnrollment: EnrollmentJointDisclosuresSubmitOutput }
  return response.submitJointEnrollment
}

export const useSubmitEnrollmentJointDisclosuresData = createMutationHook<
  EnrollmentJointDisclosuresSubmitInput,
  EnrollmentJointDisclosuresSubmitOutput
>(submitEnrollmentJointDisclosuresData)
